import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, MessageCircle, Copy, Mail, Phone, MapPin, Linkedin, Globe2, Briefcase, Sparkles } from "lucide-react";
import api from "../lib/api";
import Avatar from "../components/Avatar";
import { relativeTime } from "../lib/format";

const STATUS_FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

function StatusTag({ status }) {
  const map = {
    pending: { dot: "#C9A84C", label: "Pending" },
    approved: { dot: "#7CC274", label: "Approved" },
    rejected: { dot: "#9A9590", label: "Rejected" },
  };
  const s = map[status] || map.pending;
  return (
    <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#9A9590]">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
      {s.label}
    </span>
  );
}

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [approveResult, setApproveResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/applications").then((r) => setApps(r.data));
  useEffect(() => { load(); }, []);

  const visible = apps.filter((a) => (filter === "all" ? true : a.status === filter));
  const pendingCount = apps.filter((a) => a.status === "pending").length;

  const approve = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/applications/${id}/approve`);
      setApproveResult(data);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };
  const reject = async (id) => {
    if (!window.confirm("Reject this application?")) return;
    setBusy(true);
    await api.post(`/applications/${id}/reject`);
    setSelected(null);
    load();
    setBusy(false);
  };

  return (
    <div className="space-y-8" data-testid="applications-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="overline">Inbox</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F0EDE8] mt-2 tracking-tight">Applications</h1>
          <p className="text-[#9A9590] mt-3 max-w-xl">
            Profiles submitted by your friends before joining the table. Approve to create their member account
            and send them their login by WhatsApp.
          </p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-3">
          <Link to="/onboarding" target="_blank" className="btn-ghost-gold rounded-md px-4 py-2 text-xs uppercase tracking-widest" data-testid="open-onboarding">
            Open onboarding link →
          </Link>
          <button
            onClick={() => {
              const url = `${window.location.origin}/onboarding`;
              navigator.clipboard.writeText(url);
            }}
            className="text-xs text-[#9A9590] hover:text-[#C9A84C] transition inline-flex items-center gap-2"
            data-testid="copy-onboarding-link"
          >
            <Copy size={12}/> Copy public link
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((s) => {
          const count = s.key === "all" ? apps.length : apps.filter((a) => a.status === s.key).length;
          return (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest transition ${
                filter === s.key
                  ? "bg-[#C9A84C] text-[#0D0D0D]"
                  : "border border-[#C9A84C]/25 text-[#9A9590] hover:border-[#C9A84C]/55 hover:text-[#C9A84C]"
              }`}
              data-testid={`filter-${s.key}`}
            >
              {s.label} ({count})
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* List */}
        <div className="lg:col-span-2 space-y-3" data-testid="applications-list">
          {visible.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelected(a)}
              className={`w-full text-left ts-card p-5 transition ${selected?.id === a.id ? "border-[#C9A84C]/60" : ""}`}
              data-testid={`app-row-${a.id}`}
            >
              <div className="flex items-start gap-3">
                <Avatar name={a.name} color="#C9A84C" size={40}/>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#F0EDE8] truncate">{a.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] mt-1">{a.sector}</div>
                  <div className="text-xs text-[#9A9590] mt-1">{relativeTime(a.created_at)}</div>
                </div>
                <StatusTag status={a.status}/>
              </div>
            </button>
          ))}
          {visible.length === 0 && (
            <div className="ts-card p-8 text-center text-[#9A9590]">
              {filter === "pending"
                ? "No pending applications. Share the onboarding link to start receiving."
                : "Nothing here."}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-3">
          {selected ? (
            <ApplicationDetail
              app={selected}
              busy={busy}
              onApprove={() => approve(selected.id)}
              onReject={() => reject(selected.id)}
            />
          ) : (
            <div className="ts-card p-12 text-center text-[#9A9590]" data-testid="applications-empty">
              {pendingCount > 0
                ? `Select one of the ${pendingCount} pending application${pendingCount > 1 ? "s" : ""} on the left.`
                : "Pick an application to review the full profile."}
            </div>
          )}
        </div>
      </div>

      {approveResult && (
        <ApprovedModal
          result={approveResult}
          onClose={() => { setApproveResult(null); setSelected(null); }}
        />
      )}
    </div>
  );
}

function ApplicationDetail({ app, busy, onApprove, onReject }) {
  const Row = ({ icon: Icon, label, value }) =>
    value ? (
      <div className="flex items-start gap-3 py-2 border-b border-[#C9A84C]/8 last:border-0">
        <Icon size={14} className="text-[#C9A84C] mt-1 shrink-0" strokeWidth={1.5}/>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#9A9590]">{label}</div>
          <div className="text-sm text-[#F0EDE8] break-words whitespace-pre-wrap mt-0.5">{value}</div>
        </div>
      </div>
    ) : null;

  return (
    <div className="ts-card p-7 sm:p-9 space-y-6 animate-fade-in" data-testid="application-detail">
      <div className="flex items-start gap-4 pb-5 border-b border-[#C9A84C]/15">
        <Avatar name={app.name} color="#C9A84C" size={56}/>
        <div className="flex-1 min-w-0">
          <div className="overline">{app.sector}</div>
          <h2 className="font-serif text-2xl text-[#F0EDE8] mt-1">{app.name}</h2>
          <div className="text-xs text-[#9A9590] mt-1">{app.current_role || "—"}</div>
        </div>
        <StatusTag status={app.status}/>
      </div>

      <div>
        <Row icon={MapPin} label="City" value={app.city}/>
        <Row icon={Globe2} label="Languages" value={(app.languages || []).join(" · ")}/>
        <Row icon={Briefcase} label="Sub-specialties" value={(app.sub_specialties || []).join(" · ")}/>
        <Row icon={Briefcase} label="Typical clients" value={[
          (app.typical_clients || []).join(" · "),
          app.typical_client_notes
        ].filter(Boolean).join(" — ")}/>
      </div>

      <div className="space-y-4">
        <div>
          <div className="overline">What they bring</div>
          <p className="text-[#F0EDE8] mt-2 leading-relaxed whitespace-pre-wrap">{app.what_they_bring}</p>
        </div>
        <div>
          <div className="overline">What they are looking for</div>
          <p className="text-[#F0EDE8] mt-2 leading-relaxed whitespace-pre-wrap">{app.what_they_seek}</p>
        </div>
        {app.desired_sectors?.length > 0 && (
          <div>
            <div className="overline mb-2">Wants synergy with</div>
            <div className="flex flex-wrap gap-2">
              {app.desired_sectors.map((s) => (
                <span key={s} className="text-[10px] uppercase tracking-widest text-[#C9A84C] border border-[#C9A84C]/30 rounded-full px-3 py-1">{s}</span>
              ))}
            </div>
          </div>
        )}
        {app.live_opportunity && (
          <div className="bg-[rgba(201,168,76,0.06)] border border-[#C9A84C]/25 rounded-md p-4">
            <div className="overline flex items-center gap-2"><Sparkles size={12}/> Live opportunity</div>
            <p className="text-[#F0EDE8] mt-2 leading-relaxed whitespace-pre-wrap">{app.live_opportunity}</p>
            <p className="text-[10px] text-[#9A9590]/80 mt-2 italic">Will become a draft Opportunity on approval.</p>
          </div>
        )}
        {app.why_now && (
          <div>
            <div className="overline">Why this table, why now?</div>
            <p className="text-[#F0EDE8] mt-2 leading-relaxed whitespace-pre-wrap italic">"{app.why_now}"</p>
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-[#C9A84C]/12 space-y-1">
        <Row icon={Phone} label="WhatsApp" value={app.whatsapp}/>
        <Row icon={Mail} label="Email" value={app.email}/>
        {app.linkedin && <Row icon={Linkedin} label="LinkedIn" value={app.linkedin}/>}
        <Row icon={Briefcase} label="Referred by" value={app.referrer}/>
      </div>

      <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-[#9A9590] pt-3">
        <span className={app.accept_rhythm ? "text-[#C9A84C]" : "text-red-400/70"}>● Rhythm</span>
        <span className={app.accept_rules ? "text-[#C9A84C]" : "text-red-400/70"}>● Rules</span>
        <span className={app.understand_compensation ? "text-[#C9A84C]" : "text-red-400/70"}>● Compensation TBD</span>
        {app.open_to_organizer && <span className="text-[#C9A84C]">● Open to Organizer</span>}
      </div>

      {app.status === "pending" && (
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            onClick={onApprove}
            disabled={busy}
            className="btn-gold rounded-md px-5 py-3 inline-flex items-center justify-center gap-2 flex-1 disabled:opacity-60"
            data-testid="approve-btn"
          >
            <Check size={16}/> Approve & create member
          </button>
          <button
            onClick={onReject}
            disabled={busy}
            className="btn-ghost-gold rounded-md px-5 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="reject-btn"
          >
            <X size={16}/> Reject
          </button>
        </div>
      )}
    </div>
  );
}

function ApprovedModal({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const u = result.user;
  const loginUrl = `${window.location.origin}/login`;
  const message = `Welcome to The Synergy Table, ${u.name.split(" ")[0]}.\n\nHere are your credentials:\n🔗 ${loginUrl}\n📧 ${u.email}\n🔑 ${result.temp_password}\n\nLog in and change your password from Profile. See you at the table.`;
  const waLink = `https://wa.me/${result.whatsapp_number}?text=${encodeURIComponent(message)}`;

  const copy = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="ts-card p-8 max-w-lg w-full animate-fade-in" onClick={(e) => e.stopPropagation()} data-testid="approved-modal">
        <div className="w-12 h-12 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/40 flex items-center justify-center mb-5">
          <Check size={22} className="text-[#C9A84C]" strokeWidth={2}/>
        </div>
        <div className="overline">Member created</div>
        <h2 className="font-serif text-2xl text-[#F0EDE8] mt-2">{u.name} is at the table.</h2>
        <p className="text-sm text-[#9A9590] mt-3">Send them their credentials via WhatsApp.</p>

        <pre className="mt-6 bg-[#0D0D0D] border border-[#C9A84C]/15 rounded-md p-4 text-xs text-[#F0EDE8] whitespace-pre-wrap font-sans leading-relaxed">{message}</pre>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="btn-gold rounded-md px-5 py-3 inline-flex items-center justify-center gap-2 flex-1"
            data-testid="send-whatsapp"
          >
            <MessageCircle size={16}/> Open WhatsApp
          </a>
          <button onClick={copy} className="btn-ghost-gold rounded-md px-5 py-3 inline-flex items-center justify-center gap-2" data-testid="copy-credentials">
            <Copy size={14}/> {copied ? "Copied!" : "Copy message"}
          </button>
        </div>
        <button onClick={onClose} className="mt-5 text-xs text-[#9A9590] hover:text-[#C9A84C] block mx-auto">Close</button>
      </div>
    </div>
  );
}
